import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { BookOpen, GraduationCap, Trophy, TrendingUp, Sparkles, Clock, Calendar, ChevronRight, PlayCircle } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from "recharts";
import { toast } from "sonner";

function KpiCard({ label, value, sub, icon: Icon, color = "blue", testid }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    emerald: "bg-emerald-50 text-emerald-600",
    violet: "bg-violet-50 text-violet-600",
    amber: "bg-amber-50 text-amber-600",
  };
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-2xl p-5 card-hover">
      <div className="flex items-center justify-between">
        <div className={`w-10 h-10 rounded-xl ${colors[color]} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
        <TrendingUp className="w-4 h-4 text-emerald-500" />
      </div>
      <div className="mt-4 font-display font-black text-3xl text-slate-900 tracking-tight">{value}</div>
      <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mt-1">{label}</div>
      {sub && <div className="text-xs text-slate-400 mt-2">{sub}</div>}
    </div>
  );
}

export default function StudentDashboard() {
  const [stats, setStats] = useState(null);
  const [recs, setRecs] = useState([]);
  const [enrolled, setEnrolled] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [s, r, e] = await Promise.all([
          api.get("/analytics/student"),
          api.get("/ai/recommendations"),
          api.get("/enrollments/me"),
        ]);
        setStats(s.data); setRecs(r.data); setEnrolled(e.data);
      } catch (err) { toast.error("Failed to load dashboard"); }
    })();
  }, []);

  return (
    <Layout title="Learning Overview" subtitle="Student · Dashboard">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KpiCard testid="kpi-courses" label="Courses Enrolled" value={stats?.courses_enrolled ?? "—"} icon={BookOpen} color="blue" />
        <KpiCard testid="kpi-progress" label="Avg Progress" value={`${stats?.avg_progress ?? 0}%`} icon={TrendingUp} color="emerald" />
        <KpiCard testid="kpi-quiz" label="Quiz Score" value={`${stats?.avg_quiz_pct ?? 0}%`} icon={GraduationCap} color="violet" />
        <KpiCard testid="kpi-certs" label="Certificates" value={stats?.certificates ?? 0} icon={Trophy} color="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Weekly Activity</div>
              <h2 className="font-display font-bold text-xl text-slate-900">Lessons & quiz performance</h2>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <LineChart data={stats?.activity || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="week" stroke="#64748B" fontSize={12} />
                <YAxis stroke="#64748B" fontSize={12} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0" }} />
                <Line type="monotone" dataKey="lessons" stroke="#2563EB" strokeWidth={2.5} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="quiz" stroke="#8B5CF6" strokeWidth={2.5} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-[#F5F3FF] border border-violet-200 rounded-2xl p-6" data-testid="ai-recommendations">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-xl bg-violet-500 text-white flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-violet-600">AI Curated</div>
              <h3 className="font-display font-bold text-slate-900">Recommended for you</h3>
            </div>
          </div>
          <div className="space-y-2">
            {recs.slice(0, 4).map((c) => (
              <button
                key={c.id}
                data-testid={`rec-course-${c.id}`}
                onClick={() => nav(`/courses/${c.id}`)}
                className="w-full text-left bg-white/80 hover:bg-white border border-violet-100 rounded-xl px-3 py-2.5 flex items-center gap-3 transition-colors duration-200"
              >
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-400 to-violet-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">{c.title}</div>
                  <div className="text-xs text-slate-500">{c.duration} · {c.level}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </button>
            ))}
            {recs.length === 0 && <div className="text-xs text-slate-500">No recommendations yet.</div>}
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">In Progress</div>
            <h2 className="font-display font-bold text-xl text-slate-900">Your enrolled courses</h2>
          </div>
          <Link to="/student/courses" data-testid="browse-all-courses" className="text-sm text-blue-600 hover:text-blue-700 font-medium">Browse all →</Link>
        </div>
        {enrolled.length === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center">You haven&apos;t enrolled in any course yet. Head to the catalog to get started.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {enrolled.map((e) => (
              <div key={e.id} data-testid={`enrolled-${e.course_id}`} className="border border-slate-200 rounded-2xl overflow-hidden card-hover">
                <div className="h-28 bg-gradient-to-br from-blue-500 to-violet-500 relative">
                  {e.course?.thumbnail && <img src={e.course.thumbnail} alt="" className="w-full h-full object-cover opacity-80" />}
                  <div className="absolute bottom-2 left-3 text-white text-[10px] uppercase tracking-[0.2em]">{e.course?.level}</div>
                </div>
                <div className="p-4">
                  <div className="font-semibold text-slate-900 line-clamp-1">{e.course?.title}</div>
                  <div className="text-xs text-slate-500 mt-1 flex items-center gap-3">
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{e.course?.duration}</span>
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{e.course?.class_timing}</span>
                  </div>
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-slate-500 mb-1">
                      <span>Progress</span><span>{e.progress}%</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-600" style={{ width: `${e.progress}%` }} />
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <Link to={`/courses/${e.course_id}`} data-testid={`open-course-${e.course_id}`} className="flex-1 text-center text-sm px-3 py-2 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors">Continue</Link>
                    <a href={e.course?.zoom_url} target="_blank" rel="noreferrer" data-testid={`zoom-${e.course_id}`} className="px-3 py-2 rounded-full border border-slate-200 hover:border-blue-500 text-slate-700 inline-flex items-center gap-1 text-sm">
                      <PlayCircle className="w-4 h-4" /> Zoom
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Performance</div>
        <h2 className="font-display font-bold text-xl text-slate-900 mb-4">Attendance vs Scores</h2>
        <div className="h-56">
          <ResponsiveContainer>
            <BarChart data={[
              { name: "Attendance", value: stats?.attendance_pct || 0 },
              { name: "Assignments", value: stats?.avg_assignment_score || 0 },
              { name: "Quizzes", value: stats?.avg_quiz_pct || 0 },
              { name: "Progress", value: stats?.avg_progress || 0 },
            ]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" stroke="#64748B" fontSize={12} />
              <YAxis stroke="#64748B" fontSize={12} domain={[0, 100]} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0" }} />
              <Bar dataKey="value" fill="#10B981" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </Layout>
  );
}
